import { useState } from "react";
import { api, fmtPHP, TERMS, MIN_LOAN, MAX_LOAN, computeLoan } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Save, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const err = (e) => toast.error(e?.response?.data?.detail || "Action failed");

function TermsEditor({ loan, onDone }) {
  const [amount, setAmount] = useState(loan.principal);
  const [months, setMonths] = useState(loan.term_months);
  const valid = amount >= MIN_LOAN && amount <= MAX_LOAN;
  const preview = valid ? computeLoan(+amount, months) : null;
  const save = () =>
    api.patch(`/loans/${loan.id}/terms`, { principal: +amount, term_months: months })
      .then(() => { toast.success("Loan terms updated"); onDone(); }).catch(err);
  return (
    <div className="space-y-3">
      <div className="text-sm font-semibold text-white">Adjust amount & term</div>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-xs text-slate-400">Amount (₱)</Label>
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="terms-amount-input"
            className="w-40 bg-black/30 border-white/10 text-white font-mono" />
        </div>
        <div className="flex gap-1">
          {TERMS.map((t) => (
            <button key={t} onClick={() => setMonths(t)} data-testid={`terms-term-${t}`}
              className={`px-3 h-10 rounded-md text-xs font-semibold border ${months === t ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-200" : "bg-white/5 border-white/10 text-slate-400"}`}>
              {t}m
            </button>
          ))}
        </div>
        <Button onClick={save} disabled={!valid} data-testid="terms-save-button" className="bg-emerald-600 hover:bg-emerald-500">
          <Save className="h-4 w-4 mr-1" /> Save
        </Button>
      </div>
      <div className="text-xs text-slate-500 font-mono">
        {preview ? `New monthly ${fmtPHP(preview.monthly_payment)} · total ${fmtPHP(preview.total_repayment)}` : "Amount must be ₱50,000 – ₱3,000,000"}
      </div>
    </div>
  );
}

function OtpIssuer({ loan, onDone }) {
  const [custom, setCustom] = useState("");
  const issue = () =>
    api.post(`/loans/${loan.id}/otp`, custom ? { code: custom } : {})
      .then(({ data }) => { toast.success(`Code issued: ${data.code}`); setCustom(""); onDone(); }).catch(err);
  return (
    <div className="space-y-3">
      <div className="text-sm font-semibold text-white flex items-center gap-2"><KeyRound className="h-4 w-4 text-emerald-400" /> Borrower OTP code</div>
      <div className="text-xs text-slate-400">
        Current code: <span className="font-mono text-white text-base" data-testid="loan-otp-code">{loan.otp_code || "—"}</span>
        {loan.borrower_confirmed && (
          <span className="ml-3 text-emerald-300 inline-flex items-center gap-1" data-testid="loan-otp-confirmed"><BadgeCheck className="h-3 w-3" /> Confirmed by borrower</span>
        )}
      </div>
      <div className="flex gap-2">
        <Input placeholder="Custom code" value={custom} onChange={(e) => setCustom(e.target.value.replace(/\D/g, "").slice(0, 6))}
          data-testid="otp-custom-input" className="w-48 bg-black/30 border-white/10 text-white font-mono" />
        <Button onClick={issue} data-testid="otp-issue-button" className="bg-emerald-600 hover:bg-emerald-500">
          {custom ? "Set code" : "Generate code"}
        </Button>
      </div>
      <div className="text-[11px] text-slate-500">Share this code with the borrower; they enter it in their portal to confirm the loan.</div>
    </div>
  );
}

export function LoanAdminTools({ loan, onDone }) {
  const editable = !["Fully Paid", "Rejected"].includes(loan.status) && loan.paid_amount <= 0;
  return (
    <Card className="card-glow p-5 rounded-xl bg-[#111826] border border-white/5 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="loan-admin-tools">
      {editable ? <TermsEditor key={`${loan.principal}-${loan.term_months}`} loan={loan} onDone={onDone} /> :
        <div className="text-sm text-slate-500">Terms are locked once payments are recorded.</div>}
      <OtpIssuer loan={loan} onDone={onDone} />
    </Card>
  );
}
